import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { legacyProjectTarget, legacyWorkplaceTarget } from './legacy-address';
import { translateLegacyTaskSearch } from './task-list-search';

/**
 * The old addresses (spec §6.1, and §6.9 as overridden on 2026-09-25): `/tasks` → `/workplace?view=list` carrying
 * its section, mine and recipient over as chips; `/tasks/:id` and the old detail page `/workplace/:id` → the card's
 * modal, `/workplace?item=:id`; and the old history address `/history/task/:id` → `/history/item/:id` (spec §10), so
 * a notice or a bookmark made before still opens. Each replaces the history entry, so Back does not bounce off it.
 */

export function LegacyTasksRedirect() {
  const { search } = useLocation();
  return <Navigate replace to={translateLegacyTaskSearch(search)} />;
}

/** `/workplace?item=:id`, keeping the rest of the query (a view, a filter) and the hash; the id wins over an `item`. */
function modalOf(id: string, search = '', hash = ''): string {
  const params = new URLSearchParams(search);
  params.set('item', id);
  return `/workplace?${params.toString()}${hash}`;
}

/**
 * `/tasks/:id` and `/workplace/:id` both land on the card's modal, keeping the query and the hash. `id` is given when
 * the route names the param otherwise: `/workplace/:slug` carries the old card address too.
 */
export function WorkplaceItemRedirect({ id: given }: { id?: string } = {}) {
  const { id: param = '' } = useParams();
  const id = given ?? param;
  const { search, hash } = useLocation();
  return <Navigate replace to={modalOf(id, search, hash)} />;
}

export const LegacyTaskRedirect = () => <WorkplaceItemRedirect />;

/**
 * The Space-era Workplace addresses (sharing spec §5): `/workplace/<uuid>` opens the card, `/workplace/<slug>/` and
 * `?space=` land on the board. Anything current renders `children` (nothing, on a route that only redirects).
 */
export function LegacyWorkplaceRedirect({ children = null }: { children?: ReactNode }) {
  const { pathname, search, hash } = useLocation();
  const target = legacyWorkplaceTarget(pathname, search, hash);
  return target === null ? children : <Navigate replace to={target} />;
}

/** `/projects/<slug>/<id>/…` → `/projects/<id>/…` and `/projects/<slug>` → `/projects` (sharing spec §5). */
export function LegacyProjectRedirect({ children }: { children: ReactNode }) {
  const { pathname, search, hash } = useLocation();
  const target = legacyProjectTarget(pathname, search, hash);
  return target === null ? children : <Navigate replace to={target} />;
}

export function LegacyHistoryRedirect() {
  const { id = '' } = useParams();
  return <Navigate replace to={`/history/item/${encodeURIComponent(id)}`} />;
}
